import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Divider,
  Alert,
  CircularProgress,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
  MoreVert as MoreVertIcon,
  Chat as WechatIcon,
  Smartphone as SmartphoneIcon,
  AccountCircle as AccountCircleIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { 
  login, 
  sendSmsCode, 
  smsLogin, 
  generateWechatQRCode, 
  getWechatQRCodeStatus, 
  wechatLogin 
} from '../../store/actions/authActions';

const LoginModal = ({ open, onClose, onLoginSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [loginType, setLoginType] = useState(0); // 0: 微信, 1: 手机号, 2: 用户名密码 - 默认微信登录
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false); // 新增：专门用于发送验证码的loading状态
  const [showPassword, setShowPassword] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(''); // 添加成功消息状态
  const [anchorEl, setAnchorEl] = useState(null);
  
  // 微信登录相关状态
  const [wechatQRCode, setWechatQRCode] = useState(null); // 存储二维码信息 { scene_str, qr_url, expires_in }
  const [wechatStatus, setWechatStatus] = useState(''); // 存储微信扫码状态
  const [isGenerating, setIsGenerating] = useState(false); // 防止重复生成二维码
  const [qrGenerationFailed, setQrGenerationFailed] = useState(false); // 二维码生成最终失败标记
  
  // 使用useRef来跟踪轮询定时器，确保能正确清除
  const wechatPollingRef = useRef(null);
  const pollingStartTimeRef = useRef(null); // 记录轮询开始时间
  const qrRetryCountRef = useRef(0); // 使用ref跟踪重试次数，避免useCallback依赖问题
  const retryTimeoutRef = useRef(null); // 跟踪重试定时器
  const qrGenerationFailedRef = useRef(false); // 使用ref跟踪失败状态，避免useCallback依赖问题
  
  // 使用 useRef 存储回调函数，避免因为 props 变化导致的重复请求
  const onLoginSuccessRef = useRef(onLoginSuccess);
  const onCloseRef = useRef(onClose);
  
  // 更新 ref 中的回调函数
  useEffect(() => {
    onLoginSuccessRef.current = onLoginSuccess;
    onCloseRef.current = onClose;
  }, [onLoginSuccess, onClose]);
  
  // 表单数据
  const [formData, setFormData] = useState({
    phone: '',
    verifyCode: '',
    username: '',
    password: ''
  });

  // 登录方式选项
  const loginOptions = [
    { 
      value: 0, 
      label: '微信登录', 
      icon: <WechatIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
    },
    { 
      value: 1, 
      label: '手机登录', 
      icon: <SmartphoneIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
    },
    { 
      value: 2, 
      label: '账号登录', 
      icon: <AccountCircleIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
    }
  ];

  // 处理键盘事件
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  // 处理表单输入
  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError(''); // 清除错误信息
    setSuccess(''); // 清除成功信息
  };

  // 停止轮询微信扫码状态
  const stopWechatStatusPolling = useCallback(() => {
    if (wechatPollingRef.current) {
      console.log('停止微信轮询定时器:', wechatPollingRef.current);
      clearTimeout(wechatPollingRef.current);
      wechatPollingRef.current = null;
    }
    // 清理轮询开始时间
    pollingStartTimeRef.current = null;
    
    // 清理重试定时器
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  // 开始轮询微信扫码状态
  const startWechatStatusPolling = useCallback((sceneStr) => {
    // 清除现有的轮询
    if (wechatPollingRef.current) {
      console.log('清除现有轮询定时器:', wechatPollingRef.current);
      clearTimeout(wechatPollingRef.current);
    }
    
    // 记录轮询开始时间
    pollingStartTimeRef.current = Date.now();
    
    const pollStatus = async () => {
      try {
        const statusData = await dispatch(getWechatQRCodeStatus(sceneStr));
        
        //console.log('微信状态查询结果:', statusData);
        setWechatStatus(statusData.status);
        
        switch (statusData.status) {
          case 'pending':
            // setSuccess('请使用微信扫描二维码'); // 移除这个提示消息
            break;
          case 'scanned':
            // setSuccess('已扫码，请在微信中确认登录');
            break;
          case 'confirmed':
            // 登录成功
            setSuccess('登录成功！');
            if (wechatPollingRef.current) {
              //console.log('登录成功，清除轮询定时器:', wechatPollingRef.current);
              clearTimeout(wechatPollingRef.current);
              wechatPollingRef.current = null;
            }
            
            // 处理登录成功
            try {
              const loginResult = await dispatch(wechatLogin(statusData));
              //console.log('微信登录成功:', loginResult);
              onLoginSuccessRef.current && onLoginSuccessRef.current(loginResult.data);
              onCloseRef.current();
            } catch (loginError) {
              //console.error('微信登录处理失败:', loginError);
              setError(loginError.message || '登录处理失败');
            }
            break;
          case 'expired':
            setError('二维码已过期，请重新获取');
            if (wechatPollingRef.current) {
              console.log('二维码过期，清除轮询定时器:', wechatPollingRef.current);
              clearTimeout(wechatPollingRef.current);
              wechatPollingRef.current = null;
            }
            setWechatQRCode(null);
            break;
          case 'not_found':
            setError('二维码不存在或已过期');
            if (wechatPollingRef.current) {
              console.log('二维码不存在，清除轮询定时器:', wechatPollingRef.current);
              clearTimeout(wechatPollingRef.current);
              wechatPollingRef.current = null;
            }
            setWechatQRCode(null);
            break;
          default:
            break;
        }
      } catch (error) {
        console.error('查询微信状态失败:', error);
        setError(error.message || '查询状态失败');
        if (wechatPollingRef.current) {
          console.log('查询失败，清除轮询定时器:', wechatPollingRef.current);
          clearTimeout(wechatPollingRef.current);
          wechatPollingRef.current = null;
        }
      }
    };
    
    // 动态轮询间隔函数
    const scheduleNextPoll = () => {
      if (!pollingStartTimeRef.current) return;
      
      const elapsed = Date.now() - pollingStartTimeRef.current;
      let interval;
      
      if (elapsed < 30000) {
        // 前30秒：每1.5秒查询一次
        interval = 1500;
      } else if (elapsed < 120000) {
        // 30-120秒：每3秒查询一次
        interval = 3000;
      } else {
        // 120秒后：每5秒查询一次
        interval = 5000;
      }
      
      console.log(`设置轮询间隔: ${interval}ms (已运行: ${Math.floor(elapsed/1000)}s)`);
      
      wechatPollingRef.current = setTimeout(() => {
        pollStatus().then(() => {
          // 继续安排下次查询，直到轮询被手动停止
          if (pollingStartTimeRef.current) {
            scheduleNextPoll();
          }
        }).catch((error) => {
          console.error('轮询查询失败:', error);
          // 即使查询失败，也继续轮询（除非手动停止）
          if (pollingStartTimeRef.current) {
            scheduleNextPoll();
          }
        });
      }, interval);
    };
    
    // 立即执行一次查询
    pollStatus().then(() => {
      // 首次查询完成后开始动态轮询
      if (pollingStartTimeRef.current) {
        //console.log('首次查询完成，开始动态轮询');
        scheduleNextPoll();
      }
    }).catch((error) => {
      //console.error('首次状态查询失败:', error);
      // 即使首次查询失败，也开始轮询
      if (pollingStartTimeRef.current) {
        //console.log('首次查询失败，仍开始动态轮询');
        scheduleNextPoll();
      }
    });
    
    //console.log('启动微信动态轮询，场景:', sceneStr);
  }, [dispatch]); // 移除了 onLoginSuccess, onClose 依赖

  // 生成微信二维码（使用useCallback避免无限循环）
  const generateQRCode = useCallback(async (isRetry = false) => {
    // 清除之前的重试定时器
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    
    // 防止重复生成
    if (isGenerating) {
      console.log('正在生成二维码，跳过重复请求');
      return;
    }
    
    // 如果已经最终失败，不再重试
    if (qrGenerationFailedRef.current && !isRetry) {
      console.log('二维码生成已最终失败，跳过请求');
      return;
    }
    
    // 检查重试次数限制
    if (isRetry && qrRetryCountRef.current >= 3) {
      console.log('二维码生成重试次数已达上限(3次)');
      setError('二维码生成失败，请稍后再试或刷新页面');
      setQrGenerationFailed(true); // 标记为最终失败
      qrGenerationFailedRef.current = true; // 同时更新ref
      setLoading(false); // 最终失败时停止loading
      return;
    }
    
    setIsGenerating(true);
    // 只在首次请求时设置loading，重试时保持loading状态
    if (!isRetry) {
      setLoading(true);
    }
    setError('');
    setSuccess('');
    setQrGenerationFailed(false); // 重置失败标记
    qrGenerationFailedRef.current = false; // 同时更新ref
    
    try {
      //console.log(`开始生成微信二维码... ${isRetry ? `(重试第${qrRetryCountRef.current + 1}次)` : ''}`);
      const qrCodeData = await dispatch(generateWechatQRCode('login'));
      
      setWechatQRCode(qrCodeData);
      setWechatStatus('pending');
      setLoading(false); // 成功时停止loading
      
      // 重置重试计数和失败标记
      qrRetryCountRef.current = 0;
      setQrGenerationFailed(false);
      qrGenerationFailedRef.current = false; // 同时更新ref
      setIsGenerating(false); // 成功时重置
      
      //console.log('微信二维码生成成功:', qrCodeData);
      
      // 开始轮询状态
      startWechatStatusPolling(qrCodeData.scene_str);
      
    } catch (error) {
      // 重试过程中不停止loading，保持loading状态
      //console.error('生成微信二维码失败:', error);
      
      const currentRetryCount = isRetry ? qrRetryCountRef.current + 1 : 1;
      qrRetryCountRef.current = currentRetryCount;
      
      if (currentRetryCount < 3) {
        // 还可以重试，保持loading状态，不重置isGenerating
        console.log(`准备重试生成二维码 (${currentRetryCount}/3)`);
        // 重试过程中不显示错误信息，保持loading状态
        
        // 延迟1秒后重试
        retryTimeoutRef.current = setTimeout(() => {
          generateQRCode(true);
        }, 1000);
      } else {
        // 重试次数已达上限，停止loading并显示错误
        console.log('二维码生成重试次数已达上限');
        setError('网络连接失败，请稍后再试');
        setQrGenerationFailed(true); // 标记为最终失败
        qrGenerationFailedRef.current = true; // 同时更新ref
        setIsGenerating(false); // 最终失败时重置
        setLoading(false); // 最终失败时停止loading
      }
    }
  }, [dispatch, startWechatStatusPolling, isGenerating]); // 移除qrGenerationFailed依赖

  // 刷新微信二维码
  const refreshWechatQRCode = () => {
    stopWechatStatusPolling();
    setWechatQRCode(null);
    setWechatStatus('');
    setIsGenerating(false); // 重置生成状态
    qrRetryCountRef.current = 0; // 重置重试计数
    setQrGenerationFailed(false); // 重置失败标记
    qrGenerationFailedRef.current = false; // 同时更新ref
    generateQRCode();
  };

  // 处理登录方式切换
  const handleLoginTypeChange = (type) => {
    console.log('切换登录方式:', type, '当前轮询定时器:', wechatPollingRef.current);
    
    // 检查是否是微信登录且当前已经是微信登录模式
    const isWechatReselect = type === 0 && loginType === 0;
    
    setLoginType(type);
    setAnchorEl(null);
    setError('');
    setSuccess(''); // 清除成功信息
    
    // 停止微信轮询
    stopWechatStatusPolling();
    setWechatQRCode(null);
    setWechatStatus('');
    setIsGenerating(false); // 重置生成状态
    qrRetryCountRef.current = 0; // 重置重试计数
    setQrGenerationFailed(false); // 重置失败标记
    qrGenerationFailedRef.current = false; // 同时更新ref
    
    // 重置表单数据
    setFormData({
      phone: '',
      verifyCode: '',
      username: '',
      password: ''
    });
    
    // 如果切换到微信登录，生成二维码
    if (type === 0) {
      // 延迟一小段时间确保状态重置完成
      setTimeout(() => {
        generateQRCode();
      }, 100);
    }
    
    // 如果是重新选择微信登录，强制触发状态更新
    if (isWechatReselect) {
      console.log('重新选择微信登录，强制重置状态');
      // 强制触发重新渲染
      setLoading(false);
    }
  };

  // 发送验证码
  const handleSendCode = async () => {
    if (!formData.phone) {
      setError('请输入手机号');
      return;
    }
    
    if (!/^1[3-9]\d{9}$/.test(formData.phone)) {
      setError('请输入正确的手机号');
      return;
    }

    setSendingCode(true); // 使用专门的发送验证码loading状态
    setError('');
    setSuccess('');
    
    try {
      const response = await dispatch(sendSmsCode(formData.phone, 'login'));
      
      setSendingCode(false); // 关闭发送验证码loading状态
      setCountdown(60);
      
      // 倒计时
      const timer = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // 显示成功消息，如果是白名单手机号则显示验证码
      if (response.data.isWhitelist && response.data.verificationCode) {
        console.log('验证码（白名单）:', response.data.verificationCode);
        setSuccess(`验证码已发送，白名单验证码为：${response.data.verificationCode}`);
      } else {
        setSuccess('验证码已发送，请查收短信');
      }
      setError(''); // 清除可能的错误信息
      
    } catch (error) {
      setSendingCode(false); // 关闭发送验证码loading状态
      console.error('发送验证码失败:', error);
      setError(error.message || '发送验证码失败');
      setSuccess(''); // 清除成功信息
    }
  };

  // 手机号登录
  const handlePhoneLogin = async () => {
    if (!formData.phone || !formData.verifyCode) {
      setError('请输入手机号和验证码');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess(''); // 清除成功信息
    
    try {
      // 使用真实的手机号登录action
      const response = await dispatch(smsLogin(formData.phone, formData.verifyCode));
      
      setLoading(false);
      
      // 登录成功
      console.log('手机号登录成功:', response);
      onLoginSuccess && onLoginSuccess(response.data);
      onClose();
      
    } catch (error) {
      setLoading(false);
      console.error('手机号登录失败:', error);
      
      // 显示错误信息
      if (error.response && error.response.data && error.response.data.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('登录失败，请检查手机号和验证码');
      }
    }
  };

  // 用户名密码登录
  const handlePasswordLogin = async () => {
    if (!formData.username || !formData.password) {
      setError('请输入用户名和密码');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess(''); // 清除成功信息
    
    try {
      // 使用真实的登录action
      const response = await dispatch(login(formData.username, formData.password));
      
      setLoading(false);
      
      // 登录成功
      console.log('登录成功:', response);
      onLoginSuccess && onLoginSuccess(response.data);
      onClose();
    } catch (error) {
      setLoading(false);
      console.error('登录失败:', error);
      
      // 显示错误信息
      if (error.response && error.response.data && error.response.data.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('登录失败，请检查用户名和密码');
      }
    }
  };

  // 处理登录提交
  const handleSubmit = () => {
    switch (loginType) {
      case 0:
        // 微信登录：如果还没有二维码且没有最终失败，生成一个
        if (!wechatQRCode && !qrGenerationFailed) {
          generateQRCode();
        }
        // 如果已经最终失败，不做任何操作，保持失败状态显示
        break;
      case 1:
        handlePhoneLogin();
        break;
      case 2:
        handlePasswordLogin();
        break;
      default:
        break;
    }
  };

  // 重置表单
  const handleClose = () => {
    // 停止微信轮询
    stopWechatStatusPolling();
    
    setFormData({
      phone: '',
      verifyCode: '',
      username: '',
      password: ''
    });
    setError('');
    setSuccess(''); // 清除成功信息
    setCountdown(0);
    setLoading(false);
    setSendingCode(false); // 重置发送验证码loading状态
    setLoginType(0); // 重置为微信登录
    setAnchorEl(null);
    
    // 清除微信相关状态
    setWechatQRCode(null);
    setWechatStatus('');
    setIsGenerating(false); // 重置生成状态
    qrRetryCountRef.current = 0; // 重置重试计数
    setQrGenerationFailed(false); // 重置失败标记
    qrGenerationFailedRef.current = false; // 同时更新ref
    
    onClose();
  };

  // React useEffect 来处理组件初始化时的微信二维码生成
  useEffect(() => {
    // 当登录弹窗打开且是微信登录模式时，生成二维码
    // 但如果已经最终失败，则不再触发
    if (open && loginType === 0 && !wechatQRCode && !isGenerating && !qrGenerationFailed) {
      console.log('触发微信二维码生成，条件:', { 
        open, 
        loginType, 
        hasQRCode: !!wechatQRCode, 
        isGenerating, 
        qrGenerationFailed 
      });
      generateQRCode();
    }
  }, [open, loginType, wechatQRCode, isGenerating, qrGenerationFailed]); // 添加qrGenerationFailed依赖

  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      stopWechatStatusPolling();
    };
  }, [stopWechatStatusPolling]);

  // 获取当前登录方式的标题
  const getCurrentLoginTitle = () => {
    const currentOption = loginOptions.find(option => option.value === loginType);
    return currentOption ? currentOption.label : '微信登录';
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxWidth: '480px',
          margin: 2
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* 头部 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 3,
            pb: 2,
            position: 'relative'
          }}
        >
          {/* 左上角切换按钮 */}
          <Box>
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              size="small"
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{
                sx: {
                  borderRadius: 2,
                  minWidth: 140,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              {loginOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => handleLoginTypeChange(option.value)}
                  selected={loginType === option.value}
                  sx={{
                    py: 1.5,
                    px: 2,
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(67, 83, 255, 0.1)',
                      color: theme.palette.primary.main,
                      fontWeight: 600
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {option.icon}
                    {option.label}
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* 标题 - 需要考虑左侧按钮的平衡 */}
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              {getCurrentLoginTitle()}
            </Typography>
          </Box>

          {/* 右上角关闭按钮 */}
          <Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* 登录内容 */}
        <Box sx={{ p: 3, pt: 1 }}>
          {/* 提示信息 */}
          {error && loginType !== 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* 微信登录 */}
          {loginType === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {loading && !wechatQRCode ? (
                // 正在生成二维码（包括重试过程）
                <Box sx={{ py: 4 }}>
                  <CircularProgress size={60} sx={{ color: theme.palette.primary.main, mb: 2 }} />
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    正在生成二维码...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    
                  </Typography>
                </Box>
              ) : wechatQRCode ? (
                // 显示二维码
                <Box>
                  <Box
                    sx={{
                      width: 200,
                      height: 200,
                      mx: 'auto',
                      mb: 2,
                      border: '1px solid #eee',
                      borderRadius: 2,
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#fff'
                    }}
                  >
                    <img 
                      src={wechatQRCode.qr_url} 
                      alt="微信登录二维码" 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain' 
                      }} 
                    />
                  </Box>
                  
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {wechatStatus === 'scanned' ? '请在微信中确认' : '扫码登录'}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {wechatStatus === 'scanned' 
                      ? '已扫码，请在微信中点击确认登录' 
                      : '使用微信扫描二维码即可登录'
                    }
                  </Typography>
                  
                </Box>
              ) : qrGenerationFailed ? (
                // 二维码生成失败时显示错误信息
                <Box sx={{ py: 4 }}>
                  
                  {/* 错误图标 */}
                  <Box sx={{ mb: 2 }}>
                    <ErrorIcon 
                      sx={{ 
                        fontSize: '60px', 
                        color: theme.palette.error.main, 
                        mb: 0,
                        fontWeight: 300
                      }}
                    />
                  </Box>
                  
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'error.main' }}>
                    请求失败
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    请稍后再试或使用其它登录方式
                  </Typography>
                
                </Box>
              ) : (
                // 初始状态：没有二维码时显示的内容
                <Box sx={{ py: 4 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    
                  </Typography>
                 
                </Box>
              )}
            </Box>
          )}

          {/* 手机号登录 */}
          {loginType === 1 && (
            <Box sx={{ py: 2 }}>
              <TextField
                fullWidth
                label="手机号"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                onKeyPress={handleKeyPress}
                placeholder="请输入手机号"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <TextField
                  flex={1}
                  label="验证码"
                  value={formData.verifyCode}
                  onChange={handleInputChange('verifyCode')}
                  onKeyPress={handleKeyPress}
                  placeholder="请输入验证码"
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleSendCode}
                  disabled={countdown > 0 || sendingCode}
                  sx={{
                    minWidth: '120px',
                    height: '56px',
                    textTransform: 'none'
                  }}
                >
                  {sendingCode ? (
                    <CircularProgress size={20} />
                  ) : countdown > 0 ? (
                    `${countdown}s`
                  ) : (
                    '发送验证码'
                  )}
                </Button>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : '登录'}
              </Button>
            </Box>
          )}

          {/* 用户名密码登录 */}
          {loginType === 2 && (
            <Box sx={{ py: 2 }}>
              <TextField
                fullWidth
                label="用户名"
                value={formData.username}
                onChange={handleInputChange('username')}
                onKeyPress={handleKeyPress}
                placeholder="请输入用户名"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="密码"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleInputChange('password')}
                onKeyPress={handleKeyPress}
                placeholder="请输入密码"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : '登录'}
              </Button>
            </Box>
          )}

          {/* 底部提示 */}
          <Divider sx={{ my: 3 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', lineHeight: 1.5 }}
          >
            登录即表示您同意我们的
            <Typography 
              component="span" 
              variant="body2"
              color="primary" 
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={() => window.open('/terms', '_blank')}
            >
              用户协议
            </Typography>
            和
            <Typography 
              component="span" 
              variant="body2"
              color="primary" 
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={() => window.open('/privacy', '_blank')}
            >
              隐私政策
            </Typography>
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal; 